import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ListFilter } from "lucide-react";
import SelectField from "../../../components/inputs/SelectField";
import useApiClient from "../../../api/client";

export default function SearchCard({
    value = { q: "", cat: "" },
    onQueryChange,
    onCategoryChange,
    modelValue = "",
    onModelChange,
    modelOptions,
    onSearch,
    options,
    title = "ค้นหาสินค้า",
    subtitle = "สินค้าทั้งหมดในร้านเบสท์โฟนเชียงใหม่",
    queryPlaceholder = "ค้นหาชื่อสินค้า",
    productPlaceholder = "ทุกรุ่น",
    modelPlaceholder = "เลือกรุ่น",
}) {
    const api = useApiClient();
    const apiRef = useRef(api);
    const [categoryMasterdata, setCategoryMasterdata] = useState([]);
    const [modelMasterdata, setModelMasterdata] = useState([]);

    // ✅ เพิ่ม state สำหรับเช็ค focus ของ input
    const [isFocus, setIsFocus] = useState(false);
    const q = value?.q || "";
    const cat = value?.cat || "";
    const model = modelValue || "";

    const categoryId = useMemo(() => {
        const raw = String(cat || "").trim();
        if (!raw) return "";
        if (raw.startsWith("category:")) return raw.split(":")[1] || "";
        return "";
    }, [cat]);

    useEffect(() => {
        let ignore = false;

        (async () => {
            try {
                const categoriesRes = await apiRef.current.get("categories");

                if (ignore) return;

                const categoryList = categoriesRes?.data?.data || categoriesRes?.data?.rows || categoriesRes?.data?.items || [];

                setCategoryMasterdata(Array.isArray(categoryList) ? categoryList : []);
            } catch (e) {
                if (ignore) return;
                console.error(e);
                setCategoryMasterdata([]);
            }
        })();

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        let ignore = false;

        (async () => {
            try {
                if (!categoryId) {
                    setModelMasterdata([]);
                    return;
                }

                const res = await apiRef.current.get("models", {
                    "filters[category][id][$eq]": categoryId,
                    "pagination[page]": 1,
                    "pagination[pageSize]": 500,
                    sort: "sequence:asc",
                });

                if (ignore) return;

                const list = res?.data?.data || res?.data?.rows || res?.data?.items || [];
                setModelMasterdata(Array.isArray(list) ? list : []);
            } catch (e) {
                if (ignore) return;
                console.error(e);
                setModelMasterdata([]);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [categoryId]);

    const categoryOptions = useMemo(
        () => {
            const explicitOptions = Array.isArray(options)
                ? options
                    .map((it) => ({
                        value: String(it?.value || ""),
                        label: String(it?.label || "").trim(),
                    }))
                    .filter((it) => it.value || it.label)
                : [];

            const mappedCategoriesFromMaster = categoryMasterdata.map((it) => ({
                value: `category:${String(it?.id ?? "")}`,
                label: String(it?.name || "").trim(),
            }));

            const mappedCategories =
                explicitOptions.length > 0
                    ? explicitOptions
                    : mappedCategoriesFromMaster;

            const unique = new Map();

            mappedCategories.forEach((it) => {
                const label = String(it?.label || "").trim();
                const value = String(it?.value || "");
                if (!label && value) return;
                if (!unique.has(value)) unique.set(value, { value, label: label || "-" });
            });

            return Array.from(unique.values());
        },
        [categoryMasterdata, options]
    );

    const resolvedModelOptions = useMemo(() => {
        const hasExplicit = Array.isArray(modelOptions) && modelOptions.length > 0;
        const input = hasExplicit
            ? modelOptions
            : modelMasterdata.map((it) => ({
                value: String(it?.id ?? ""),
                label: String(it?.name || "").trim(),
            }));
        const unique = new Map();

        input
            .map((it) => ({
                value: String(it?.value || ""),
                label: String(it?.label || "").trim(),
            }))
            .filter((it) => it.value || it.label)
            .forEach((it) => {
                const value = String(it.value || "");
                const label = String(it.label || "").trim();
                if (!value && !label) return;
                if (!unique.has(value)) unique.set(value, { value, label: label || "-" });
            });

        return [{ value: "", label: "ทุกรุ่น" }, ...Array.from(unique.values())];
    }, [modelOptions, modelMasterdata]);

    const showModelSelect = Boolean(onModelChange);

    const handleCategoryChange = (next) => {
        onCategoryChange?.(next);
        onModelChange?.("");
    };

    return (
        <div className="bg-white rounded-xl border border-slate-100 py-6 sm:px-6 md:px-9 shadow-[#3E403333] drop-shadow-xl">
            <div className="text-xl sm:text-2xl font-medium text-[#1F1F1D]">
                {title}
            </div>
            <div className="text-sm text-[#1F1F1DB2] mt-1">
                {subtitle}
            </div>

            {/* ===== MOBILE UI ===== */}
            <div className="mt-4 space-y-3 md:hidden">
                <div className="grid grid-cols-12 gap-3">
                    <div className={showModelSelect ? "col-span-6" : "col-span-12"}>
                        <SelectField
                            icon={ListFilter}
                            options={categoryOptions}
                            value={cat}
                            onChange={handleCategoryChange}
                            placeholder={productPlaceholder}
                            buttonClassName="h-12 rounded-xl"
                            menuClassName="z-[80]"
                        />
                    </div>

                    {showModelSelect ? (
                        <div className="col-span-6">
                            <SelectField
                                icon={ListFilter}
                                options={resolvedModelOptions}
                                value={model}
                                onChange={onModelChange}
                                placeholder={modelPlaceholder}
                                buttonClassName="h-12 rounded-xl"
                                menuClassName="z-[80]"
                            />
                        </div>
                    ) : null}
                </div>

                <div
                    className={[
                        "flex items-center gap-3 h-12 rounded-xl bg-white px-4 transition",
                        isFocus ? "border-[3px] border-[#047DED33]" : "border border-slate-200",
                    ].join(" ")}
                >
                    <Search className="w-5 h-5 text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => onQueryChange?.(e.target.value)}
                        placeholder={queryPlaceholder}
                        className="w-full text-sm outline-none placeholder:text-slate-400"
                        onFocus={() => setIsFocus(true)}
                        onBlur={() => setIsFocus(false)}
                    />
                </div>

                <button
                    type="button"
                    onClick={() => onSearch?.()}
                    className="h-12 w-full rounded-xl bg-[#047DED] text-white font-semibold hover:bg-[#3EA2FE] active:scale-[0.99] transition"
                >
                    ค้นหา
                </button>
            </div>

            {/* ===== DESKTOP UI (เหมือนเดิม) ===== */}
            <div className="mt-4 hidden md:block">
                <div
                    className={[
                        "flex items-stretch rounded-xl bg-white transition",
                        isFocus ? "border-[2px] border-[#047DED4D]" : "border border-[#1D161633]",
                    ].join(" ")}
                >
                    {/* LEFT: dropdown (product) */}
                    <div
                        className={
                            (showModelSelect ? "w-[220px]" : "w-[250px]") +
                            " border-r border-[#FFF] flex-shrink-0 p-1 pr-4"
                        }
                    >
                        <SelectField
                            icon={ListFilter}
                            options={categoryOptions}
                            value={cat}
                            onChange={handleCategoryChange}
                            placeholder={productPlaceholder}
                            buttonClassName="h-14 border-none"
                            menuClassName="z-[8000]"
                        />
                    </div>

                    {/* MID-LEFT: dropdown (model) */}
                    {showModelSelect ? (
                        <div className="w-[220px] border-r border-[#1D161633] flex-shrink-0 p-1 pr-4">
                            <SelectField
                                icon={ListFilter}
                                options={resolvedModelOptions}
                                value={model}
                                onChange={onModelChange}
                                placeholder={modelPlaceholder}
                                buttonClassName="h-14 border-none"
                                menuClassName="z-[8000]"
                            />
                        </div>
                    ) : null}

                    {/* MID: input */}
                    <div className="flex-1 flex items-center gap-2 px-4 h-14 mt-1 hover:text-[#047DED] placeholder:text-[#1D1616B2]">
                        <Search className="w-5 h-5" />
                        <input
                            value={q}
                            onChange={(e) => onQueryChange?.(e.target.value)}
                            placeholder={queryPlaceholder}
                            className="w-full text-sm outline-none placeholder:text-[#1D1616B2] text-[#1D1616B2]"
                            onFocus={() => setIsFocus(true)}
                            onBlur={() => setIsFocus(false)}
                        />
                    </div>

                    {/* RIGHT: button */}
                    <button
                        type="button"
                        onClick={() => onSearch?.()}
                        className="h-16 w-[140px] bg-[#047DED] text-white font-medium hover:bg-[#3EA2FE] transition rounded-r-xl"
                    >
                        ค้นหา
                    </button>
                </div>
            </div>
        </div>
    );
}
